import { useEffect, useState, type ReactNode } from 'react'
import { COLORS } from '../../lib/theme'
import { Icon } from '../Icon'

/**
 * The prototype's report chrome, ported rather than approximated.
 *
 * Everything here exists to match `apps/wehunt-web-prototype/src/components/
 * huntReport/` — the bounded panel, the gradient header, the compact stat bar and
 * the collapsible sections. The first attempt at this page invented its own
 * layout (a centred stack of plain cards), which read as a different product
 * even though it showed the same numbers.
 */

/** Wide/narrow switch on the report's own breakpoint, matching the prototype. */
export function useIsNarrow(breakpoint = 900): boolean {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < breakpoint,
  )
  useEffect(() => {
    if (typeof window === 'undefined') return
    const onResize = () => setNarrow(window.innerWidth < breakpoint)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [breakpoint])
  return narrow
}

/**
 * A collapsible section.
 *
 * The prototype gives every report section this shape, which is what makes a long
 * report scannable — a reader collapses what they are not looking at. Open by
 * default so nothing is hidden on arrival.
 */
export function ReportSection({
  title,
  defaultOpen = true,
  children,
}: {
  title: string
  defaultOpen?: boolean
  children: ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="wh-card" style={{ padding: 0, overflow: 'hidden' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '16px 18px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          font: 'inherit',
        }}
      >
        <span style={{ fontWeight: 700, fontSize: 15, flex: 1, textAlign: 'left' }}>{title}</span>
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={20} color={COLORS.outline} />
      </button>
      {open ? <div style={{ padding: '0 18px 18px' }}>{children}</div> : null}
    </div>
  )
}

// One compact card with the stats in a row, wrapping to 2×2 on a narrow card.
// The query is on the CARD, not the viewport: the report column's width varies
// independently of the window, so a viewport media query wraps at the wrong time.
const STAT_BAR_CSS = `
.hr-stat-card { container-type: inline-size; }
.hr-stat-bar { display: grid; grid-template-columns: repeat(var(--hr-stat-count, 4), minmax(0, 1fr)); }
.hr-stat { display: flex; flex-direction: column; align-items: center; text-align: center; gap: 6px; padding: 4px 10px; border-left: 1px solid var(--nl-surface-container-highest); min-width: 0; }
.hr-stat:first-child { border-left: none; }
@container (max-width: 460px) {
  .hr-stat-bar { grid-template-columns: 1fr 1fr; row-gap: 16px; }
  .hr-stat:nth-child(odd) { border-left: none; }
}
`

export interface ReportStat {
  icon: ReactNode
  value: string | number
  label: string
}

export function ReportStatBar({ stats }: { stats: ReportStat[] }) {
  return (
    <div className="wh-card hr-stat-card" style={{ padding: '14px 16px' }}>
      <style>{STAT_BAR_CSS}</style>
      {/* The count is a custom property rather than a fixed 4: this report omits
          the duration stat on a hunt that never recorded an end, and a hard-coded
          four-column grid would leave a gap where it should have been. */}
      <div className="hr-stat-bar" style={{ ['--hr-stat-count' as string]: String(stats.length) }}>
        {stats.map((stat) => (
          <div className="hr-stat" key={stat.label}>
            {stat.icon}
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontFamily: COLORS.fontDisplay,
                  fontSize: 20,
                  fontWeight: 600,
                  color: COLORS.primary,
                  lineHeight: 1.15,
                  whiteSpace: 'nowrap',
                }}
              >
                {stat.value}
              </div>
              <div
                style={{
                  fontSize: 11,
                  letterSpacing: 0.4,
                  textTransform: 'uppercase',
                  color: COLORS.outline,
                }}
              >
                {stat.label}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/** A game pin as the report renders it inline, at the prototype's sizes. The
 *  three kinds each have their own shipped art under `public/assets`
 *  (`map_pin_downed.svg`, `map_pin_wounded.svg`, `map_pin_observed.svg`) — the
 *  observed pin reuses the same art the map already draws for a sighting, so no
 *  new asset was needed for it. */
export function ReportPin({ kind, size = 26 }: { kind: 'downed' | 'wounded' | 'observed'; size?: number }) {
  return (
    <img
      src={`/assets/map_pin_${kind}.svg`}
      alt=""
      width={size}
      height={Math.round((size * 27) / 26)}
      style={{ display: 'block', flexShrink: 0 }}
    />
  )
}

/**
 * The panel the report lives in: the same chrome as the ongoing and upcoming hunt
 * views, so an ended hunt does not look like a different screen.
 */
export function ReportPanel({ header, children }: { header: ReactNode; children: ReactNode }) {
  return (
    <div
      style={{
        height: 'calc(100vh - 56px - 48px - 32px)',
        minHeight: 520,
        background: COLORS.surfaceContainerLowest,
        border: `1px solid ${COLORS.surfaceContainerHighest}`,
        borderRadius: 12,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '18px 20px 16px',
          borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`,
          background: `linear-gradient(180deg, ${COLORS.surfaceContainerHighest} 0%, ${COLORS.surfaceContainerLowest} 100%)`,
        }}
      >
        {header}
      </div>
      {children}
    </div>
  )
}
